/**
 * How It Works — slide deck content (data only, no JSX).
 *
 * The interactive tour that tells the ALIS story: one place, end to
 * end — quote → approval → work order → scenario scheduling → testing →
 * reports → a living, growing system.
 *
 * Copy rule (from the lab): never call it a "lab management system" — it is a
 * lab application. Artwork lives in /public/how-it-works/img (generated,
 * cohesive sage-palette series); audio in /public/how-it-works/audio.
 */

import type { TourSlideBase } from './tourCore';

export interface TourSlideDef extends TourSlideBase {
  /** Big display headline. */
  title: string;
  /** Body paragraphs, revealed with a stagger. */
  lines: string[];
  /** Optional little fact chips shown under the body. */
  chips?: string[];
}

export const TOUR_SLIDES: TourSlideDef[] = [
  {
    id: 'hero',
    kicker: 'ALIS',
    title: "Welcome to the lab's new home.",
    lines: [
      'This is the story of how your work flows through one place — from the first quote to the final report.',
      'It takes about two minutes. There are sounds. There is confetti.',
    ],
    image: '/how-it-works/img/hero.webp',
    alt: 'A welcoming laboratory at golden hour with a path leading to the door',
    accent: 'sage',
  },
  {
    id: 'scattered',
    kicker: 'The old way',
    title: 'Work used to live everywhere.',
    lines: [
      'One system for proposals. Another for quotes. KeyLAB for running tests — itself an Excel add-in. Excel again for scheduling. Something else for invoicing.',
      'Five tools, zero conversations between them — and nobody could see the whole picture.',
    ],
    image: '/how-it-works/img/scattered.webp',
    alt: 'Disconnected islands of spreadsheets and paper forms tied by tangled strings',
    accent: 'night',
    chips: ['Proposals ⟂ Quotes', 'Tests ⟂ Schedule', 'No centralized view'],
  },
  {
    id: 'one-place',
    kicker: 'The big idea',
    title: 'One place. End to end.',
    lines: [
      "ALIS isn't just testing and reports — that part existed before.",
      "It's the whole journey under one roof: quotes, approvals, work orders, scheduling, testing, reporting. Above and beyond.",
    ],
    image: '/how-it-works/img/one-place.webp',
    alt: 'Scattered islands converging into one glowing central hub',
    accent: 'sage',
  },
  {
    id: 'two-doors',
    kicker: 'Where work begins',
    title: 'Every job starts at one of two doors.',
    lines: [
      'Door one: a quote — priced, routed for approval, accepted. Door two: straight to a work order.',
      'Different starts, same destination. Nothing falls through the cracks on the way.',
    ],
    image: '/how-it-works/img/two-doors.webp',
    alt: 'Two doorways whose paths merge into a single road toward the horizon',
    accent: 'moss',
  },
  {
    id: 'quote',
    kicker: 'Quotes & approvals',
    title: 'A “yes” becomes work — instantly.',
    lines: [
      'Build the quote, send it through approval, and the moment the client accepts, it becomes a work order.',
      "No re-typing. No copy-paste. The quote's tests carry straight through to the lab floor.",
    ],
    image: '/how-it-works/img/quote.webp',
    alt: 'A proposal document transforming into a work-order clipboard',
    accent: 'amber',
  },
  {
    id: 'workorder',
    kicker: 'The work order',
    title: 'The heart of the job.',
    lines: [
      'Locations, samples, and the tests to run on them — one living record the whole lab shares.',
      'Add samples, attach tests, and everything downstream already knows.',
    ],
    image: '/how-it-works/img/workorder.webp',
    alt: 'A folder at the center of map pins, core samples and lab flasks',
    accent: 'sage',
    chips: ['Locations', 'Samples', 'Tests'],
  },
  {
    id: 'scheduler',
    kicker: 'The scenario planner',
    title: 'Scheduling that thinks with you.',
    lines: [
      'It knows your machines, your people, their skills and their availability — even who needs an experienced BGCer at their side.',
      "It drafts the schedule and flags the conflicts. Then you drag, drop and fine-tune until it's exactly right.",
    ],
    image: '/how-it-works/img/scheduler.webp',
    alt: 'A hand dragging a bar on a large planning board with machines and people below',
    accent: 'slate',
    chips: ['Machines', 'Skills', 'People', 'Capacity', 'Drag & drop'],
  },
  {
    id: 'skills',
    kicker: 'People & skills',
    title: 'The right person. The right machine.',
    lines: [
      'Every test needs skills, and every person has a level — 1 to 5.',
      'Still training? The planner pairs you with an experienced BGCer automatically. Trainers, workload, vacations — all part of the plan.',
    ],
    image: '/how-it-works/img/skills.webp',
    alt: 'An experienced worker guiding a trainee, with skill badges matching people to machines',
    accent: 'moss',
  },
  {
    id: 'testing',
    kicker: 'Run the tests',
    title: 'You test. It computes.',
    lines: [
      'Record readings — or upload them — and the curves, stresses and classifications appear as you go.',
      'The math is the lab’s own, verified against the spreadsheets you already trust.',
    ],
    image: '/how-it-works/img/testing.webp',
    alt: 'A press machine compressing a rock core while a curve draws itself on screen',
    accent: 'slate',
  },
  {
    id: 'reports',
    kicker: 'Reports',
    title: 'From readings to a polished PDF.',
    lines: [
      'One click renders the full report — one shared house style across every test, from Direct Shear to Cerchar.',
      'Review it, sign it off, send it out.',
    ],
    image: '/how-it-works/img/reports.webp',
    alt: 'Finished reports gliding out of a friendly printing machine',
    accent: 'amber',
  },
  {
    id: 'living',
    kicker: 'Always growing',
    title: 'It grows with the lab.',
    lines: [
      'A new test to onboard? It gets added. Something feels off? Feedback is built right into the app — tell us and watch it change.',
      "Updates ship every week. Check the version chip — it's probably newer than yesterday.",
    ],
    image: '/how-it-works/img/living.webp',
    alt: 'A tree growing from a lab flask with gears and beakers as fruit',
    accent: 'sage',
  },
  {
    id: 'finale',
    kicker: "That's ALIS",
    title: 'One lab. One place. Welcome aboard.',
    lines: [
      'Quote to schedule to test to report — no more islands.',
      'Now go see it for yourself.',
    ],
    image: '/how-it-works/img/finale.webp',
    alt: 'Confetti fireworks over the laboratory, every window glowing',
    accent: 'night',
  },
];
